import React, { useState } from 'react';
import { Trophy, Edit, Search, Info } from 'lucide-react';
import { Player, Config } from '../../types';
import { collection, addDoc, doc, updateDoc, increment } from 'firebase/firestore';
import { RankingService } from '../../services/rankingService';

interface AdminRankingCircuitoProps {
  userId: string;
  players: Player[];
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
  logoUrl?: string;
}

export function AdminRankingCircuito({ userId, players, db, getCollectionPath, config, logoUrl }: AdminRankingCircuitoProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [pointsAdjustment, setPointsAdjustment] = useState<string>('');
  const [adjustmentReason, setAdjustmentReason] = useState<string>('Ajuste manual del organizador');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 1. Filtrar jugadores que tengan puntos en el club/organizador actual
  const circuitPlayers = players.filter(p => {
    const points = p.rankingPoints?.clubs?.[userId] || 0;
    return points > 0;
  });

  // 2. Ordenar por puntos (mayor a menor)
  const sortedPlayers = [...circuitPlayers].sort((a, b) => {
    const pointsA = a.rankingPoints?.clubs?.[userId] || 0;
    const pointsB = b.rankingPoints?.clubs?.[userId] || 0;
    return pointsB - pointsA;
  });

  // 3. Filtrar por búsqueda
  const filteredPlayers = sortedPlayers.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAdjustPoints = async () => {
    if (!editingPlayer || !pointsAdjustment) return;
    const points = parseInt(pointsAdjustment);
    if (isNaN(points) || points === 0) return;

    setIsSubmitting(true);
    try {
      // 1. Añadir el ajuste al historial (pointsHistory)
      const historyRef = collection(db, getCollectionPath('players'), editingPlayer.id, 'pointsHistory');
      await addDoc(historyRef, {
        points: points,
        type: 'adjustment',
        reason: adjustmentReason,
        organizerId: userId,
        isGlobal: false, // Solo para el circuito
        createdAt: Date.now()
      });

      // 2. Crear una notificación para el jugador
      const notifRef = collection(db, getCollectionPath('notifications'));
      await addDoc(notifRef, {
        userId: editingPlayer.id,
        title: 'Ajuste de Ranking Local',
        body: `Se ha realizado un ajuste manual en tu ranking del circuito: ${points > 0 ? '+' : ''}${points} pts. Motivo: ${adjustmentReason}`,
        points: points,
        type: 'ranking_points',
        read: false,
        createdAt: Date.now()
      });

      // 3. Recalcular los puntos usando el servicio (este recalcula todo basándose en el historial)
      await RankingService.recalculatePlayerPoints(editingPlayer.id);

      setEditingPlayer(null);
      setPointsAdjustment('');
    } catch (error) {
      console.error('Error al ajustar puntos:', error);
      alert('Hubo un error al guardar el ajuste.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300 relative z-10 p-2 md:p-6 bg-[#1a1c23] rounded-2xl border border-white/5 shadow-2xl">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black theme-primary-text uppercase italic tracking-tight flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-8 h-8 object-contain rounded-md drop-shadow-md" />
            ) : (
              <Trophy size={24} />
            )}
            Ranking del Circuito
          </h2>
          <p className="text-[10px] md:text-xs text-zinc-400 font-bold uppercase tracking-widest mt-1">
            Controla y gestiona el ranking de tu circuito.
          </p>
        </div>
        
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={16} />
          <input
            type="text"
            placeholder="Buscar jugador o categoría..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-sm text-white font-bold outline-none focus:border-theme-primary transition-all placeholder-white/30"
          />
        </div>
      </div>

      <div className="bg-black/20 rounded-2xl border border-white/5 overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[500px]">
            <thead>
              <tr className="bg-white/5 border-b border-white/10 text-[9px] md:text-[10px] uppercase tracking-widest font-black text-white/50">
                <th className="p-3 md:p-4 w-16 text-center">Pos</th>
                <th className="p-3 md:p-4">Jugador</th>
                <th className="p-3 md:p-4">Categoría</th>
                <th className="p-3 md:p-4 text-center">Puntos (Circuito)</th>
                <th className="p-3 md:p-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredPlayers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-white/40 text-xs font-bold uppercase tracking-widest">
                    No se encontraron jugadores en tu circuito.
                  </td>
                </tr>
              ) : (
                filteredPlayers.map((player, index) => {
                  const points = player.rankingPoints?.clubs?.[userId] || 0;
                  return (
                    <tr key={player.id} className="hover:bg-white/5 transition-colors group">
                      <td className="p-3 md:p-4 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-black text-sm italic ${
                          index === 0 ? 'bg-yellow-500 text-black shadow-[0_0_10px_rgba(234,179,8,0.3)]' :
                          index === 1 ? 'bg-slate-300 text-black shadow-[0_0_10px_rgba(203,213,225,0.3)]' :
                          index === 2 ? 'bg-amber-700 text-white shadow-[0_0_10px_rgba(180,83,9,0.3)]' :
                          'bg-white/10 text-white/70'
                        }`}>
                          {index + 1}°
                        </span>
                      </td>
                      <td className="p-3 md:p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-black border border-white/10 overflow-hidden shrink-0">
                            {player.photoUrl ? (
                              <img src={player.photoUrl} alt={player.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-white/5 text-white/30 font-black text-xs">
                                {player.name.charAt(0)}
                              </div>
                            )}
                          </div>
                          <span className="text-xs md:text-sm font-bold text-white group-hover:text-theme-primary transition-colors">
                            {player.name}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 md:p-4">
                        <span className="px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest border border-white/20 text-white/70 bg-white/5">
                          {player.category || 'Sin Cat'}
                        </span>
                      </td>
                      <td className="p-3 md:p-4 text-center">
                        <span className="text-sm md:text-base font-black text-white bg-black/40 px-3 py-1.5 rounded-lg border border-white/10 shadow-inner">
                          {points}
                        </span>
                      </td>
                      <td className="p-3 md:p-4 text-center">
                        <button
                          onClick={() => setEditingPlayer(player)}
                          className="p-2 bg-white/5 hover:bg-theme-primary/20 text-white/50 hover:text-theme-primary rounded-lg transition-all border border-transparent hover:border-theme-primary/30"
                          title="Ajustar puntos"
                        >
                          <Edit size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Ajuste de Puntos */}
      {editingPlayer && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !isSubmitting && setEditingPlayer(null)}></div>
          <div className="bg-[#0f172a] border border-theme-primary/30 w-full max-w-sm rounded-[2rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] relative z-10 p-6 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center gap-3 mb-6 mt-2">
              <div className="w-16 h-16 rounded-full bg-black border-2 border-theme-primary overflow-hidden">
                {editingPlayer.photoUrl ? (
                  <img src={editingPlayer.photoUrl} alt={editingPlayer.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/5 text-theme-primary font-black text-xl">
                    {editingPlayer.name.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-lg font-black text-white italic uppercase tracking-tight leading-tight">{editingPlayer.name}</h3>
                <p className="text-[10px] text-theme-primary font-bold uppercase tracking-widest mt-1">
                  Puntos Actuales: {editingPlayer.rankingPoints?.clubs?.[userId] || 0}
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="text-[10px] uppercase font-black tracking-widest text-white/50 block mb-1.5 ml-1">
                  Ajuste (+ o -)
                </label>
                <input
                  type="number"
                  placeholder="Ej: 50 o -20"
                  value={pointsAdjustment}
                  onChange={(e) => setPointsAdjustment(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-xl p-3 text-sm text-white font-bold outline-none focus:border-theme-primary transition-colors placeholder-white/20 text-center"
                />
              </div>
              
              <div>
                <label className="text-[10px] uppercase font-black tracking-widest text-white/50 block mb-1.5 ml-1">
                  Motivo del ajuste
                </label>
                <input
                  type="text"
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-xl p-3 text-xs text-white font-medium outline-none focus:border-theme-primary transition-colors"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setEditingPlayer(null)}
                disabled={isSubmitting}
                className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-all disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAdjustPoints}
                disabled={!pointsAdjustment || isSubmitting}
                className="flex-[1.5] theme-primary-bg text-black py-3 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
              >
                {isSubmitting ? 'Guardando...' : 'Aplicar Ajuste'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
